import { useId, useState } from "react";
import { StyleSheet, View, type LayoutChangeEvent } from "react-native";
import Svg, {
  Defs,
  RadialGradient,
  Rect,
  Stop
} from "react-native-svg";

import { radius } from "@/design/tokens";

type EdgeSize = {
  height: number;
  width: number;
};

type SpecularEdgeProps = {
  cornerRadius?: number;
  solidColor?: string;
};

const GLINT_CENTER_X = 0.2;
const GLINT_CENTER_Y = -0.18;
const GLINT_RADIUS_X = 1.45;
const GLINT_RADIUS_Y = 1.35;
const STROKE_WIDTH = 1;

/**
 * The shared specular hairline used by dark inset action surfaces on web,
 * iOS, and Android. Its dimensions and focus match:
 * `radial-gradient(145% 135% at 20% -18%, ...)`.
 */
export function SpecularEdge({
  cornerRadius = radius.lg,
  solidColor
}: SpecularEdgeProps) {
  const gradientId = `specular-edge-${useId().replace(/:/g, "")}`;
  const [size, setSize] = useState<EdgeSize>({ height: 0, width: 0 });

  const handleLayout = (event: LayoutChangeEvent) => {
    const { height, width } = event.nativeEvent.layout;
    setSize((current) =>
      current.height === height && current.width === width
        ? current
        : { height, width }
    );
  };

  const gradientCenterY = size.height * GLINT_CENTER_Y;
  const gradientRadiusX = size.width * GLINT_RADIUS_X;
  const gradientScaleY =
    gradientRadiusX > 0
      ? (size.height * GLINT_RADIUS_Y) / gradientRadiusX
      : 1;

  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      onLayout={handleLayout}
      pointerEvents="none"
      style={StyleSheet.absoluteFill}
    >
      {size.width > 0 && size.height > 0 ? (
        <Svg height={size.height} pointerEvents="none" width={size.width}>
          {!solidColor ? (
            <Defs>
              <RadialGradient
                cx={size.width * GLINT_CENTER_X}
                cy={gradientCenterY}
                fx={size.width * GLINT_CENTER_X}
                fy={gradientCenterY}
                gradientTransform={[
                  1,
                  0,
                  0,
                  gradientScaleY,
                  0,
                  gradientCenterY * (1 - gradientScaleY)
                ]}
                gradientUnits="userSpaceOnUse"
                id={gradientId}
                r={gradientRadiusX}
              >
                <Stop
                  offset="0%"
                  stopColor="rgb(210, 220, 255)"
                  stopOpacity={0.12}
                />
                <Stop
                  offset="28%"
                  stopColor="rgb(210, 220, 255)"
                  stopOpacity={0.12}
                />
                <Stop
                  offset="62%"
                  stopColor="rgb(167, 178, 216)"
                  stopOpacity={0.08}
                />
                <Stop
                  offset="100%"
                  stopColor="rgb(167, 178, 216)"
                  stopOpacity={0.08}
                />
              </RadialGradient>
            </Defs>
          ) : null}
          <Rect
            fill="none"
            height={size.height - STROKE_WIDTH}
            rx={cornerRadius - STROKE_WIDTH / 2}
            ry={cornerRadius - STROKE_WIDTH / 2}
            stroke={solidColor ?? `url(#${gradientId})`}
            strokeWidth={STROKE_WIDTH}
            width={size.width - STROKE_WIDTH}
            x={STROKE_WIDTH / 2}
            y={STROKE_WIDTH / 2}
          />
        </Svg>
      ) : null}
    </View>
  );
}
